import React from "react";
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  Typography,
  Spin,
  Alert,
  Popconfirm,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  UserSwitchOutlined,
  MailOutlined,
  BankOutlined,
  IdcardOutlined,
  SafetyCertificateOutlined,
  EnvironmentOutlined,
  KeyOutlined,
  CopyOutlined,
  ReloadOutlined,
  SendOutlined,
  MobileOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { isEmail } from "validator";
import secureLocalStorage from "react-secure-storage";

import {
  adminResetPassword,
  resetUserTwoFactor,
  updateUser,
} from "../../actions/users";
import { genPassword } from "../../utils/password";
import {
  ACCESS_LEVELS,
  PERMISSIONS,
  YES_NO,
  pickPermissions,
} from "../../constants/userAccess";
import BranchSelect from "../../ui/BranchSelect";

const { Title, Text } = Typography;

function getFullName(user) {
  return `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "User";
}

export default function EditUser() {
  const { id } = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState(() => genPassword(8));
  const [resetting, setResetting] = React.useState(false);

  // Admins change their own password from Settings (needs current password)
  const isSelf = React.useMemo(() => {
    try {
      return (
        JSON.parse(secureLocalStorage.getItem("profile"))?.results?._id === id
      );
    } catch {
      return false;
    }
  }, [id]);

  const copyNewPassword = async () => {
    try {
      await navigator.clipboard.writeText(newPassword);
      toast.success("Password copied");
    } catch (error) {
      toast.error("Couldn't copy, please copy it manually");
    }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      await dispatch(adminResetPassword(id, newPassword));
      // Don't leave the used password on screen for reuse
      setNewPassword(genPassword(8));
    } catch (error) {
      // Already toasted by the action
    } finally {
      setResetting(false);
    }
  };

  const [removingApp, setRemovingApp] = React.useState(false);
  const removeAuthenticatorApp = async () => {
    setRemovingApp(true);
    try {
      await dispatch(resetUserTwoFactor(id));
    } catch (error) {
      // Already toasted by the action
    } finally {
      setRemovingApp(false);
    }
  };

  const users = useSelector((state) => state.users || []);

  const selectedUser = React.useMemo(() => {
    return users.find((user) => user._id === id);
  }, [users, id]);

  React.useEffect(() => {
    if (!selectedUser) return;

    form.setFieldsValue({
      firstName: selectedUser.firstName || "",
      lastName: selectedUser.lastName || "",
      email: selectedUser.email || "",
      branch: selectedUser.branch || undefined,
      position: selectedUser.position || "",

      ...PERMISSIONS.reduce((acc, permission) => {
        acc[permission.key] = selectedUser[permission.key] || "Restricted";
        return acc;
      }, {}),

      hr_permission: selectedUser.hr_permission || "no",
      payroll_permission: selectedUser.payroll_permission || "no",
    });
  }, [selectedUser, form]);

  const submitUpdateUser = async () => {
    try {
      const values = await form.validateFields();

      if (!isEmail(values.email)) {
        toast.error("Please enter a valid email address");
        return;
      }

      if (!selectedUser?._id) {
        toast.error("User not found");
        return;
      }

      setSubmitting(true);

      const payload = {
        name: `${values.firstName || ""} ${values.lastName || ""}`.trim(),
        email: values.email,

        firstName: values.firstName,
        lastName: values.lastName,
        branch: values.branch,
        position: values.position,

        ...pickPermissions(values),
      };

      await dispatch(updateUser(selectedUser._id, payload));

      navigate("/admin");
    } catch (error) {
      // Validation errors show on the form; request errors were already
      // toasted by the action, so just stay on the page.
    } finally {
      setSubmitting(false);
    }
  };

  const loadingUser = users.length === 0 && !selectedUser;

  if (loadingUser) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background:
            "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
        }}
      >
        <Spin size="large" tip="Loading user..." />
      </div>
    );
  }

  if (!selectedUser) {
    return (
      <div
        style={{
          minHeight: "100vh",
          padding: "60px 18px",
          background:
            "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
        }}
      >
        <div style={{ maxWidth: 900, margin: "50px auto" }}>
          <Alert
            type="error"
            showIcon
            message="User not found"
            description="The selected user could not be found. Please go back to the users list and try again."
          />

          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin")}
            style={{
              marginTop: 18,
              borderRadius: 12,
              fontWeight: 700,
            }}
          >
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "70px 18px",
        background:
          "linear-gradient(135deg, #eef4ff 0%, #f8fafc 45%, #eefdf5 100%)",
      }}
    >
      <div
        style={{
          maxWidth: 1180,
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Space size={14}>
            <div
              style={{
                width: 54,
                height: 54,
                borderRadius: 18,
                background: "linear-gradient(135deg, #1677ff, #4096ff)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 12px 28px rgba(22, 119, 255, .28)",
              }}
            >
              <UserSwitchOutlined style={{ color: "#fff", fontSize: 26 }} />
            </div>

            <div>
              <Title level={3} style={{ margin: 0, fontWeight: 900 }}>
                Edit User
              </Title>

              <Text type="secondary">
                Update profile information and platform permissions.
              </Text>
            </div>
          </Space>

          <Button
            size="large"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/admin")}
            style={{
              borderRadius: 12,
              fontWeight: 700,
            }}
          >
            Back to Users
          </Button>
        </div>

        <Row gutter={[18, 18]}>
          {/* Main form */}
          <Col xs={24} lg={17}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
                overflow: "hidden",
              }}
              bodyStyle={{ padding: 0 }}
            >
              <div
                style={{
                  padding: "18px 22px",
                  background:
                    "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                  color: "#fff",
                }}
              >
                <Space>
                  <IdcardOutlined style={{ fontSize: 20 }} />

                  <div>
                    <Text style={{ color: "#dbeafe", fontWeight: 800 }}>
                      User Information
                    </Text>

                    <div style={{ color: "#94a3b8", fontSize: 12 }}>
                      Editing: {getFullName(selectedUser)}
                    </div>
                  </div>
                </Space>
              </div>

              <div style={{ padding: 22 }}>
                <Form layout="vertical" form={form}>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        name="firstName"
                        label="First name"
                        rules={[
                          {
                            required: true,
                            message: "First name is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter first name"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="lastName"
                        label="Last name"
                        rules={[
                          {
                            required: true,
                            message: "Last name is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          placeholder="Enter last name"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="email"
                        label="User email"
                        rules={[
                          {
                            required: true,
                            message: "Email is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          prefix={<MailOutlined />}
                          placeholder="example@email.com"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        name="branch"
                        label="User branch"
                        rules={[
                          {
                            required: true,
                            message: "Branch is required",
                          },
                        ]}
                      >
                        <BranchSelect
                          size="large"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24}>
                      <Form.Item
                        name="position"
                        label="Position"
                        rules={[
                          {
                            required: true,
                            message: "Position is required",
                          },
                        ]}
                      >
                        <Input
                          size="large"
                          prefix={<BankOutlined />}
                          placeholder="Enter user position"
                          style={{ borderRadius: 12 }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Card>
          </Col>

          {/* Side summary */}
          <Col xs={24} lg={7}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              }}
              bodyStyle={{ padding: 22 }}
            >
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <div>
                  <Tag
                    color={selectedUser.login === "yes" ? "green" : "red"}
                    style={{
                      borderRadius: 999,
                      padding: "4px 12px",
                      fontWeight: 800,
                    }}
                  >
                    {selectedUser.login === "yes" ? "ONLINE" : "OFFLINE"}
                  </Tag>

                  <Title level={4} style={{ marginTop: 12, marginBottom: 4 }}>
                    {getFullName(selectedUser)}
                  </Title>

                  <Text type="secondary">
                    {selectedUser.email || "No email available"}
                  </Text>
                </div>

                <Divider style={{ margin: 0 }} />

                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: "#eff6ff",
                    border: "1px solid #bfdbfe",
                  }}
                >
                  <Space>
                    <EnvironmentOutlined style={{ color: "#1d4ed8" }} />
                    <Text strong style={{ color: "#1d4ed8" }}>
                      Branch
                    </Text>
                  </Space>

                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">
                      {selectedUser.branch || "No branch assigned"}
                    </Text>
                  </div>
                </div>

                <div
                  style={{
                    padding: 14,
                    borderRadius: 18,
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                  }}
                >
                  <Text strong style={{ color: "#15803d" }}>
                    Permission Update
                  </Text>

                  <div style={{ marginTop: 6 }}>
                    <Text type="secondary">
                      Changes made here will affect the user’s platform access.
                    </Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Permissions */}
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
              }}
              bodyStyle={{ padding: 22 }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                <Space>
                  <SafetyCertificateOutlined
                    style={{ fontSize: 22, color: "#1677ff" }}
                  />

                  <div>
                    <Title level={4} style={{ margin: 0 }}>
                      Platform Permissions
                    </Title>

                    <Text type="secondary">
                      Edit access levels for each platform.
                    </Text>
                  </div>
                </Space>
              </div>

              <Form layout="vertical" form={form}>
                <Row gutter={[16, 16]}>
                  {PERMISSIONS.map((permission) => (
                    <Col key={permission.key} xs={24} sm={12} md={8} lg={6}>
                      <div
                        style={{
                          padding: 14,
                          borderRadius: 18,
                          border: "1px solid #e5e7eb",
                          background:
                            "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                        }}
                      >
                        <Form.Item
                          name={permission.key}
                          label={
                            <Text strong style={{ color: "#334155" }}>
                              {permission.label}
                            </Text>
                          }
                          initialValue="Restricted"
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            size="large"
                            style={{ width: "100%" }}
                            options={ACCESS_LEVELS.map((level) => ({
                              label: level,
                              value: level,
                            }))}
                          />
                        </Form.Item>
                      </div>
                    </Col>
                  ))}

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        border: "1px solid #e5e7eb",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <Form.Item
                        name="hr_permission"
                        label={
                          <Text strong style={{ color: "#334155" }}>
                            HR Right
                          </Text>
                        }
                        initialValue="no"
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          size="large"
                          options={YES_NO.map((x) => ({
                            label: x.toUpperCase(),
                            value: x,
                          }))}
                        />
                      </Form.Item>
                    </div>
                  </Col>

                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 18,
                        border: "1px solid #e5e7eb",
                        background:
                          "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                      }}
                    >
                      <Form.Item
                        name="payroll_permission"
                        label={
                          <Text strong style={{ color: "#334155" }}>
                            Payroll Right
                          </Text>
                        }
                        initialValue="no"
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          size="large"
                          options={YES_NO.map((x) => ({
                            label: x.toUpperCase(),
                            value: x,
                          }))}
                        />
                      </Form.Item>
                    </div>
                  </Col>
                </Row>
              </Form>
            </Card>
          </Col>

          {/* Reset password */}
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
              }}
              bodyStyle={{ padding: 22 }}
            >
              <Space style={{ marginBottom: 14 }}>
                <KeyOutlined style={{ fontSize: 22, color: "#1677ff" }} />
                <div>
                  <Title level={4} style={{ margin: 0 }}>
                    Reset Password
                  </Title>
                  <Text type="secondary">
                    Sets a new password, signs the user out of every app and
                    emails them the new password.
                  </Text>
                </div>
              </Space>

              {isSelf ? (
                <Alert
                  type="info"
                  showIcon
                  message="To change your own password, use Settings from the Services menu."
                />
              ) : (
                <div
                  style={{
                    padding: 14,
                    borderRadius: 16,
                    background: "#f1f5f9",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Space wrap>
                    <Text strong>New password:</Text>
                    <Tag
                      color="blue"
                      style={{
                        padding: "4px 12px",
                        borderRadius: 999,
                        fontWeight: 800,
                        letterSpacing: 1,
                        fontFamily: "monospace",
                        fontSize: 14,
                      }}
                    >
                      {newPassword}
                    </Tag>
                  </Space>

                  <Space wrap>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={copyNewPassword}
                      style={{ borderRadius: 12, fontWeight: 700 }}
                    >
                      Copy
                    </Button>
                    <Button
                      icon={<ReloadOutlined />}
                      onClick={() => setNewPassword(genPassword(8))}
                      style={{ borderRadius: 12, fontWeight: 700 }}
                    >
                      Regenerate
                    </Button>
                    <Popconfirm
                      title={`Reset ${getFullName(selectedUser)}'s password?`}
                      description={`They will be signed out and the new password emailed to ${selectedUser.email}.`}
                      okText="Reset & email"
                      okButtonProps={{ danger: true }}
                      onConfirm={resetPassword}
                    >
                      <Button
                        type="primary"
                        danger
                        icon={<SendOutlined />}
                        loading={resetting}
                        style={{ borderRadius: 12, fontWeight: 800 }}
                      >
                        Reset & Email
                      </Button>
                    </Popconfirm>
                  </Space>
                </div>
              )}
            </Card>
          </Col>

          {/* Authenticator app (only once it has been set up) */}
          {selectedUser.twoFactorEnabled && (
            <Col xs={24}>
              <Card
                bordered={false}
                style={{
                  borderRadius: 24,
                  boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
                }}
                bodyStyle={{ padding: 22 }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Space>
                    <MobileOutlined style={{ fontSize: 22, color: "#15803d" }} />
                    <div>
                      <Title level={4} style={{ margin: 0 }}>
                        Authenticator App{" "}
                        <Tag color="green" style={{ marginLeft: 6 }}>
                          On
                        </Tag>
                      </Title>
                      <Text type="secondary">
                        Lost phone? Remove the app so they can sign in and set
                        up a new one.
                      </Text>
                    </div>
                  </Space>
                  <Popconfirm
                    title={`Remove ${getFullName(selectedUser)}'s authenticator app?`}
                    description="They will sign in with their password only until they set it up again (or be asked to set it up, if it is required)."
                    okText="Remove"
                    okButtonProps={{ danger: true }}
                    onConfirm={removeAuthenticatorApp}
                  >
                    <Button
                      danger
                      loading={removingApp}
                      style={{ borderRadius: 12, fontWeight: 800 }}
                    >
                      Remove authenticator app
                    </Button>
                  </Popconfirm>
                </div>
              </Card>
            </Col>
          )}

          {/* Actions */}
          <Col xs={24}>
            <Card
              bordered={false}
              style={{
                borderRadius: 24,
                boxShadow: "0 18px 45px rgba(15, 23, 42, .08)",
              }}
              bodyStyle={{
                padding: 18,
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="large"
                onClick={() => navigate("/admin")}
                style={{
                  borderRadius: 12,
                  fontWeight: 700,
                }}
              >
                Cancel
              </Button>

              <Button
                size="large"
                type="primary"
                icon={<SaveOutlined />}
                loading={submitting}
                onClick={submitUpdateUser}
                style={{
                  borderRadius: 12,
                  fontWeight: 800,
                  boxShadow: "0 10px 20px rgba(22, 119, 255, .25)",
                }}
              >
                Save Changes
              </Button>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
